import type { Editor } from "grapesjs";
import headerHtml from "../../../../snippets/header/default.html?raw";
import heroHtml from "../../../../snippets/hero/overlay-blue.html?raw";
import calloutHtml from "../../../../snippets/callout/red-bordered.html?raw";
import threeColsHtml from "../../../../snippets/columns/three-icons.html?raw";
import ctaRedHtml from "../../../../snippets/button/cta-red.html?raw";
import ctaBlueHtml from "../../../../snippets/button/cta-blue.html?raw";
import dividerHtml from "../../../../snippets/divider/red-line.html?raw";
import socialHtml from "../../../../snippets/social/default.html?raw";
import footerHtml from "../../../../snippets/footer/default.html?raw";

const CATEGORY = { id: "tvs", label: "Bloques TVS", open: true };

const ICON_BASE =
  "<svg viewBox='0 0 24 24' width='22' height='22' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>";

const ICONS: Record<string, string> = {
  header: `${ICON_BASE}<rect x='3' y='4' width='18' height='6' rx='1'/><line x1='3' y1='14' x2='21' y2='14'/></svg>`,
  hero: `${ICON_BASE}<rect x='3' y='3' width='18' height='14' rx='2'/><circle cx='9' cy='9' r='1'/><path d='M21 15l-5-5L5 21'/></svg>`,
  callout: `${ICON_BASE}<rect x='3' y='5' width='18' height='14' rx='2'/><line x1='7' y1='5' x2='7' y2='19'/></svg>`,
  columns: `${ICON_BASE}<rect x='3' y='4' width='5' height='16'/><rect x='10' y='4' width='4' height='16'/><rect x='16' y='4' width='5' height='16'/></svg>`,
  cta: `${ICON_BASE}<rect x='3' y='8' width='18' height='8' rx='2'/><line x1='8' y1='12' x2='16' y2='12'/></svg>`,
  divider: `${ICON_BASE}<line x1='3' y1='12' x2='21' y2='12'/></svg>`,
  social: `${ICON_BASE}<circle cx='5' cy='12' r='2'/><circle cx='12' cy='12' r='2'/><circle cx='19' cy='12' r='2'/></svg>`,
  footer: `${ICON_BASE}<rect x='3' y='14' width='18' height='6' rx='1'/><line x1='3' y1='10' x2='21' y2='10'/></svg>`,
};

interface BlockDef {
  id: string;
  label: string;
  description: string;
  content: string;
  icon: string;
}

const BLOCKS: BlockDef[] = [
  {
    id: "tvs-header",
    label: "Header TVS",
    description: "Logo blanco sobre azul TVS",
    content: headerHtml,
    icon: ICONS.header,
  },
  {
    id: "tvs-hero",
    label: "Hero con overlay",
    description: "Imagen + título sobre azul translúcido",
    content: heroHtml,
    icon: ICONS.hero,
  },
  {
    id: "tvs-callout",
    label: "Bloque destacado",
    description: "Caja con borde rojo + label + valor",
    content: calloutHtml,
    icon: ICONS.callout,
  },
  {
    id: "tvs-columns-3",
    label: "3 columnas con icono",
    description: "Grid 3-col responsive (stack en mobile)",
    content: threeColsHtml,
    icon: ICONS.columns,
  },
  {
    id: "tvs-cta-red",
    label: "Botón rojo (primario)",
    description: "Bulletproof button color marca",
    content: ctaRedHtml,
    icon: ICONS.cta,
  },
  {
    id: "tvs-cta-blue",
    label: "Botón azul (secundario)",
    description: "Bulletproof button azul TVS",
    content: ctaBlueHtml,
    icon: ICONS.cta,
  },
  {
    id: "tvs-divider",
    label: "Divisor rojo",
    description: "Línea horizontal de marca",
    content: dividerHtml,
    icon: ICONS.divider,
  },
  {
    id: "tvs-social",
    label: "Redes sociales",
    description: "Instagram / YT / FB / TikTok",
    content: socialHtml,
    icon: ICONS.social,
  },
  {
    id: "tvs-footer",
    label: "Footer corporativo",
    description: "Contacto + unsubscribe + copyright",
    content: footerHtml,
    icon: ICONS.footer,
  },
];

// Los snippets viven como <tr>...</tr> (pensados para insertarse dentro de un <table>
// padre del email). Si los inyectamos así en el canvas, el parser del navegador
// descarta los <tr> huérfanos y el bloque desaparece. Los envolvemos en una
// tabla `presentation` — válida y email-safe (tablas anidadas son la convención).
function wrapSnippet(html: string): string {
  // Quita comentarios HTML de cabecera (los snippets empiezan con <!-- SNIPPET ... -->).
  const stripped = html.replace(/^\s*(?:<!--[\s\S]*?-->\s*)+/i, "").trim();
  if (/^<table[\s>]/i.test(stripped)) return stripped;
  if (!/^<tr[\s>]/i.test(stripped)) return stripped;
  return (
    `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="border-collapse:collapse;width:100%;max-width:600px;margin:0 auto;">` +
    `<tbody>${stripped}</tbody></table>`
  );
}

export const tvsBlocksPlugin = (editor: Editor): void => {
  const bm = editor.BlockManager;
  for (const block of BLOCKS) {
    bm.add(block.id, {
      label: `<div style="font-size:11px;font-weight:600;line-height:1.2;">${block.label}</div>` +
        `<div style="font-size:9px;opacity:.7;line-height:1.1;margin-top:2px;">${block.description}</div>`,
      category: CATEGORY,
      content: wrapSnippet(block.content),
      media: block.icon,
      attributes: { "data-block-id": block.id },
    });
  }
};
